import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { InquiryProvider } from "./components/Inquiry";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsApp from "./components/WhatsApp";
import Seo from "./components/Seo";
import { SiteSettingsProvider } from "./SiteSettings";
import Home from "./pages/Home";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Work from "./pages/Work";
import CaseStudy from "./pages/CaseStudy";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Journal from "./pages/Journal";
import JournalArticle from "./pages/JournalArticle";
import Admin from "./pages/Admin";
import { EASE } from "./components/Motion";
import { CASE_STUDIES, JOURNAL, SERVICES } from "./data/content";

const DEFAULT_DESC =
  "Product branding & packaging design agency in Pune — food & FMCG branding, brand identity, logo design, video production and digital marketing across India.";

const META = {
  "/": ["Product Branding Agency in Pune — Oasis", DEFAULT_DESC],
  "/services": [
    "End-to-End Product Branding Services — Oasis",
    "Video production, brand identity, packaging design, product development, digital marketing and web — six crafts under one roof in Pune, working across India.",
  ],
  "/work": [
    "Work — Branding & Packaging Portfolio — Oasis",
    "Case studies and selected pieces from Oasis Business Solutions — full brand builds, packaging systems and campaigns, made to be felt.",
  ],
  "/about": [
    "About — Creative Agency in Pune — Oasis",
    "A small, senior creative agency in Pune — directors, designers and producers making work with a pulse since 2014.",
  ],
  "/contact": [
    "Contact — Start a Project — Oasis",
    "Start a project with Oasis Business Solutions — send a brief, write to the studio, or book a discovery call.",
  ],
  "/journal": [
    "Journal — Notes from the Studio — Oasis",
    "Essays and behind-the-scenes notes from the Oasis studio — on branding, packaging, film and the craft of attention.",
  ],
};

const SERVICE_SEO = {
  "video-production": [
    "Video Production Company in Pune — Oasis",
    "Video production company in Pune for brand films, product videos, product photography and launch content — cinematic craft for brands with something to say.",
  ],
  "brand-identity": [
    "Brand Identity Design Agency in Pune — Oasis",
    "Brand identity design agency in Pune — logo design services, brand strategy and consulting, and identity systems for startups and growing brands.",
  ],
  "packaging-design": [
    "Packaging Design Company in Pune — Oasis",
    "Packaging design company in Pune — food packaging design, edible oil packaging and label design for food products, from a single label to a full FMCG range.",
  ],
  "product-development": [
    "Product Packaging Design Services — Oasis",
    "End-to-end product development and product packaging design services — concept, prototyping, manufacturing liaison and launch assets from a product studio in Pune.",
  ],
  "social-media-marketing": [
    "Digital Marketing Agency in Pune — Oasis",
    "Digital marketing agency in Pune — social media management, content production and product launch marketing that turn attention into sales.",
  ],
  "web-development": [
    "D2C Brand Development & Web Design — Oasis",
    "D2C brand development and web design in Pune — fast, cinematic websites and online stores built to convert attention into orders.",
  ],
};

function RouteSeo() {
  const { pathname } = useLocation();
  let meta = META[pathname];
  let noindex = false;
  if (!meta) {
    const [, section, slug] = pathname.split("/");
    if (section === "services" && slug) {
      meta = SERVICE_SEO[slug];
      if (!meta) {
        const service = SERVICES.find((x) => x.slug === slug);
        if (service) meta = [`${service.name} — Oasis`, service.intro];
      }
    } else if (section === "work" && slug) {
      const cs = CASE_STUDIES.find((x) => x.slug === slug);
      if (cs) meta = [`${cs.title} — ${cs.scope} case study — Oasis`, cs.summary];
    } else if (section === "journal" && slug) {
      const article = JOURNAL.find((x) => x.slug === slug);
      if (article) meta = [`${article.title} — Oasis Journal`, article.excerpt];
    } else if (section === "admin") {
      meta = ["Studio Dashboard — Oasis", ""];
      noindex = true;
    }
  }
  if (!meta) meta = META["/"];
  return <Seo title={meta[0]} description={meta[1]} noindex={noindex} />;
}

function Shell() {
  const location = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="site-shell">
      <div className="grain" />
      <RouteSeo />
      <Header />
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.45, ease: EASE }}
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/work" element={<Work />} />
            <Route path="/work/:slug" element={<CaseStudy />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:slug" element={<JournalArticle />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </motion.main>
      </AnimatePresence>
      <Footer />
      <WhatsApp />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <SiteSettingsProvider>
          <InquiryProvider>
            <Shell />
          </InquiryProvider>
        </SiteSettingsProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
